/**
 * Generate a (mostly) unique ID in an RFC4122 version 4 compliant format
 *
 * @returns String
 *
 * @source https://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid
 */
export const uuidv4 = () => {
  return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  );
};

/**
 * Check if an Object exists in an Array
 *
 * @param {Array} arraytoCheck The Array to search through
 * @param {String} objectId The "id" attribute of the Object in the Array
 *
 * @returns Boolean
 */
export const objectIsInArray = (arraytoCheck, objectId) => {
  const filteredValues = arraytoCheck.filter( arrayObject => {
    return arrayObject.id === objectId;
  });
  let isInArray = true;

  if (filteredValues.length === 0) {
    isInArray = false;
  }

  return isInArray;
};

/**
 * Take an Array of data and return items that match the filtering criteria
 *
 * @param {Array} sourceArray The Array of Objects
 * @param {String} filterProperty The property of the Array child Objects to filter the outage data by. e.g. "id" or "postcode"
 * @param {String} filterValue The value to match in the filter property. If this value does not match, the Object is excluded from the result
 * @param {Integer} limit Optional. A limit to enforce on the returned data. e.g. Set this to 1, if you only want one result in your filtered Array
 * @param {Boolean} inverse Optional. Invert the condition matching. i.e. If true, then return all items that DO NOT match the provided filterValue
 *
 * @returns Array The filtered data
 */
export const filterArrayByProperty = (sourceArray, filterProperty, filterValue, limit = 0, inverse = false) => {
  let filteredArrayData = [];

  // Filter data by provided value
  if (filterValue !== "") {
    for (let i = 0, j = sourceArray.length; i < j; ++i) {
      if (limit !== 0 && filteredArrayData.length === limit) {
        break;
      } else {
        if (inverse) {
          if (sourceArray[i][filterProperty] !== filterValue) {
            filteredArrayData.push(sourceArray[i]);
          }
        } else {
          if (sourceArray[i][filterProperty] === filterValue) {
            filteredArrayData.push(sourceArray[i]);
          }
        }
      }
    }
  } else {
    // If no filter value was provided
    // then we return the entire source Array
    return sourceArray;
  }

  return filteredArrayData;
};

/**
 * Take an Array of data and remove items that do not meet the filtering criteria
 *
 * @param {Array} sourceArray The Array of Objects
 * @param {String} filterProperty The property of the Array child Objects to filter the outage data by. e.g. "id" or "postcode"
 * @param {Array} filterValues The value(s) to match in the filter property. If this value matches, the Object is excluded from the result
 *
 * @returns Array The data without the matched items
 */
export const removeItemsFromArrayByProperty = (sourceArray, filterProperty, filterValues) => {
  let updatedArrayData = [];

  updatedArrayData = sourceArray.map( (arrayItem) => {
    let markForDeletion = false;

    // Loop over the filter values
    for (let i = 0, j = filterValues.length; i < j; ++i) {
      // Check if this item matches the filter list to remove from the Array
      if (arrayItem[filterProperty] === filterValues[i]) {
        // Mark this item for removal
        markForDeletion = true;
      }
    }

    if (markForDeletion) {
      return null;
    } else {
      return arrayItem;
    }
  });

  // Remove any items marked for deletion
  updatedArrayData = updatedArrayData.filter( (arrayItem) => {
    if (arrayItem !== null) {
      return true;
    } else {
      return false;
    }
  });

  return updatedArrayData;
};

/**
 * Email address validation
 *
 * @param {String} email The email address to validate
 *
 * @returns Boolean
 *
 * @source https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
 */
export const validateEmail = (email) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

/**
 * Iterate over a form's inputs and collect the inputs
 * that have a response.
 *
 * Note: We assume validation (e.g. required, acceptable value)
 * has already been completed by this point
 *
 * @param {HTMLCollection} form The form to use when generating the payload
 *
 * @returns Array
 */
export const collectFormResponses = (form) => {
  const formInputs = form.querySelectorAll('input, textarea, select');
  const formResponses = [];
  let inputType = '';
  let inputName = '';
  let inputValue = '';
  let inputGroupId = '';
  let inputCustomOutput = '';
  let inputValidationLabel = '';
  let inputChecked = false;
  let sectionLabel = '';

  for (let item of formInputs) {

    // Capture the basic input types. e.g. text, radio, checkbox
    if (item.tagName === "INPUT") {
      inputType = item.getAttribute('type');
      inputName = item.getAttribute('name');
      inputValue = item.getAttribute('value');
      inputGroupId = item.getAttribute('data-groupid');
      inputCustomOutput = item.getAttribute('data-value-formatted');
      inputValidationLabel = item.getAttribute('data-validation-label');
      inputChecked = item.checked;
      sectionLabel = item.getAttribute('data-section-label');

      // Get the "clean name" for radio inputs
      if (inputType === 'radio' && inputChecked) {
        inputName = item.getAttribute('data-clean-name');
      }

      // Capture any custom value formats.
      // e.g. the date inputs.
      if (
        inputCustomOutput !== null &&
        inputCustomOutput !== ''
      ) {
        inputValue = inputCustomOutput;
      }

      // Default. Capture the response data
      if (
        inputName !== null && inputType === 'hidden' && inputValue !== '' ||
        inputName !== null && inputType === 'text' && inputValue !== '' ||
        inputName !== null && inputType === 'tel' && inputValue !== '' ||
        inputName !== null && inputType === 'email' && inputValue !== '' ||
        inputName !== null && inputType === 'date' && inputValue !== '' ||
        inputName !== null && inputType === 'radio' && inputChecked ||
        inputName !== null && inputType === 'checkbox' && inputChecked
      ) {
        formResponses.push({
          fieldName: inputName,
          fieldValue: inputValue,
          fieldGroup: inputGroupId,
          fieldValidationLabel: inputValidationLabel,
          sectionLabel: sectionLabel
        });
      }
    }

    // Capture textarea inputs
    if (item.tagName === "TEXTAREA") {
      inputName = item.getAttribute('name');
      inputValue = item.value;
      inputGroupId = item.getAttribute('data-groupid');
      inputValidationLabel = item.getAttribute('data-validation-label');
      sectionLabel = item.getAttribute('data-section-label');

      // Default. Capture the response data
      if (inputName !== null && inputValue !== '') {
        formResponses.push({
          fieldName: inputName,
          fieldValue: inputValue,
          fieldGroup: inputGroupId,
          fieldValidationLabel: inputValidationLabel,
          sectionLabel: sectionLabel
        });
      }
    }

    // Capture select inputs
    if (item.tagName === "SELECT") {
      inputName = item.getAttribute('name');
      inputValue = item.value;
      inputGroupId = item.getAttribute('data-groupid');
      inputValidationLabel = item.getAttribute('data-validation-label');
      sectionLabel = item.getAttribute('data-section-label');

      // Default. Capture the response data
      if (inputName !== null && inputValue !== '') {
        formResponses.push({
          fieldName: inputName,
          fieldValue: inputValue,
          fieldGroup: inputGroupId,
          fieldValidationLabel: inputValidationLabel,
          sectionLabel: sectionLabel
        });
      }
    }
  }

  return formResponses;
};

/**
 * Given a JSON Array of the form responses, convert the
 * data into a format suitable for displaying a form summary.
 *
 * We also apply some business rules here to filter out some
 * responses that are not relevant to the calculations as
 * well as replace some response values.
 *
 * @param {Array} formResponses The form data, as returned by collectFormResponses()
 *
 * @returns Array
 */
export const generateFormSummary = (formResponses) => {
  const formSummaryData = [];
  let inputGroups = {};
  let responseData = {};
  let inputSectionId = '';

  // Loop over the form responses
  for (let i = 0, j = formResponses.length; i < j; ++i) {
    inputSectionId = formResponses[i].sectionLabel;
    responseData = {
      ...formResponses[i]
    };

    // Skip the initial "acknowledgement" question
    if (responseData.fieldName === "acknowledgement") {
      continue;
    }

    /**
     * For any responses that are exactly "0", we assume
     * that these were "unanswered" as they do not factor
     * into the estimate calculation.
     */
    if (responseData.fieldValue === "0") {
      responseData.fieldValue = "No response provided";
    }

    if (inputSectionId !== null) {
      // Group fields with matching IDs together
      if (inputGroups[ inputSectionId ] !== undefined) {
        // Add to the existing group
        inputGroups[ inputSectionId ].groupFields.push({
          ...responseData
        });
      } else {
        // Create a new group
        inputGroups[ inputSectionId ] = {
          groupFields: [{
            ...responseData
          }],
          sortPosition: i
        };
      }
    } else {
      // Just capture the field data
      formSummaryData.push({
        ...responseData,
        sortPosition: i
      });
    }
  }

  // Add the sectioned fields to the summary data
  // eslint-disable-next-line no-unused-vars
  for (const [key, value] of Object.entries(inputGroups)) {
    formSummaryData.push({
      groupFields: value.groupFields,
      sortPosition: value.sortPosition
    });
  }

  // Sort the form summary data back into the correct order
  formSummaryData.sort((a,b) => (a.sortPosition > b.sortPosition) ? 1 : ((b.sortPosition > a.sortPosition) ? -1 : 0));

  return formSummaryData;
};

/**
 * Given any number, round it to the specified number of decimal places. Rounding is done to the nearest value
 * e.g. 5 = 5, 1.234 = 1.2, 8.889 = 8.9, 0.244 = 0.2, -12.887 = -12.9
 *
 * @param {Number} value The number to apply the rounding to
 * @param {Integer} decimals The number of decimal places to round the number to
 *
 * @returns Number The provided value, rounded to the specified number of decimal places
 *
 * @source https://www.jacklmoore.com/notes/rounding-in-javascript/
 */
export const roundToDecimal = (value, decimals) => {
  return Number(Math.round(value+'e'+decimals)+'e-'+decimals);
};

/**
 * Given an hour value in 12-hour format, convert it into
 * the corresponding 24-hour format.
 *
 * @param {String|Integer} twelveHourValue The hour. i.e. "1" to "12"
 * @param {String} meridiem The meridiem. Either lowercase or uppercase. i.e. "am" or "PM"
 *
 * @returns String
 */
export const convertTwelveHourToTwentyFourHour = (twelveHourValue, meridiem) => {
  const parsedHourNumber = parseInt(twelveHourValue, 10);
  const parsedMeridiemString = meridiem.toLowerCase();

  // 12am
  if (parsedHourNumber === 12 && parsedMeridiemString === 'am') {
    return '00';
  }

  // 1am - 9am
  if (parsedHourNumber < 10 && parsedMeridiemString === 'am') {
    return `0${twelveHourValue}`;
  }

  // 1pm - 11pm
  if (parsedMeridiemString === 'pm' && parsedHourNumber < 12) {
    return (twelveHourValue + 12).toString();
  }

  // 10am - 12pm
  // No change.
  return twelveHourValue;
};

/**
 * Compare two 24-hour format time strings and determine if
 * the second string occurs at the same instant or after the
 * first string
 *
 * @param {Array} timeStrings Two Strings in 24-hour format. e.g. ['09:45:00','22:04:00']
 *
 * @returns Boolean
 */
export const timeOccursOnOrAfter = (timeStrings) => {
  let isOnOrAfter = false;
  let firstTimeValues = [];
  let secondTimeValues = [];

  firstTimeValues = timeStrings[0]
                    .split(':')
                    .map(timeString => {
                      return parseInt(timeString, 10)
                    });

  secondTimeValues = timeStrings[1]
                    .split(':')
                    .map(timeString => {
                      return parseInt(timeString, 10)
                    });

  if (firstTimeValues[0] < secondTimeValues[0]) {
    // The second hour is after the first hour
    isOnOrAfter = true;
  }

  if (firstTimeValues[0] === secondTimeValues[0]) {
    // The hours are the same, check the minutes
    if (firstTimeValues[1] <= secondTimeValues[1]) {
      // The minutes are the same or the second minutes are after the first
      isOnOrAfter = true;
    }
  }

  return isOnOrAfter;
};

/**
 * Get the absolute Y (top) position of an element in the document.
 * i.e. not it's relative position to the current viewport,
 * but it's fixed position relative to the top of the document
 *
 * @param {String} elementClassName A CSS Class on the element you want to find the position of
 *
 * @returns Integer
 *
 * @source https://stackoverflow.com/questions/442404/retrieve-the-position-x-y-of-an-html-element
 */
export const getElementAbsoluteYPosition = (elementClassName) => {
  let pageElement = document.getElementsByClassName(elementClassName)[0];
  let pageElementRect = pageElement.getBoundingClientRect();
  let currentScrollTop = document.documentElement.scrollTop ? document.documentElement.scrollTop : document.body.scrollTop;
  return pageElementRect.top + currentScrollTop;
};

/**
 * Convert an Integer into a human-friendly formatted String.
 * e.g.
 * 1024 -> 1,024
 *
 * @param {Integer} integer The Integer to convert
 * @param {String} delimiter Optional. The value to use to separate the unit blocks. Defaults to a comma.
 *
 * @returns String
 */
export const integerToFriendly = (integer, delimiter = ",") => {
  const integerAsArray = [ ...integer.toString() ];

  if (integerAsArray.length > 3) {
    integerAsArray.splice(-3, 0, delimiter);
  }

  return integerAsArray.join("");
};